import React, { useCallback } from 'react'

import { useMutation, useQuery } from '@apollo/client'
import { navigate } from '@reach/router'
import distanceInWords from 'date-fns/distance_in_words'
import * as R from 'ramda'

import { Flex, Icon, SecondaryButton, Text } from 'stardust'

import Error404 from '~/components/404'
import Can from '~/components/Authorisation/Can'
import DocumentModal from '~/components/DocumentModal'
import DocumentModalPlaceholder from '~/components/DocumentModalPlaceholder'
import DocumentModalPreview, {
  EXPANDED_PREVIEW_HEIGHT,
  STACKED_PREVIEW_HEIGHT,
} from '~/components/DocumentModalPreview'
import DocumentRenderer from '~/components/DocumentRenderer'
import MediaRenderer from '~/components/MediaRenderer'
import ModalButton from '~/components/ModalButton'
import { isPlayground } from '~/config'
import { useSelectedServiceFkey } from '~/contexts/Service'
import { is404 } from '~/modules/api'
import { observations as obs } from '~/modules/permissions'
import { UPDATE_OBSERVATION_BOOKMARK } from '~/pages/Observations/mutations'

import { GET_OBSERVATION } from './queries'

const observedOnText = ({
  observationDate,
  observationTime,
}: {
  observationDate: string
  observationTime: string
}) => distanceInWords(`${observationDate} ${R.pipe(R.split('.'), R.head)(observationTime)}`, new Date())

const editorName = R.pipe<any[], any[], any, string>(
  R.filter((x: any) => x.action == 'created' || x.action == 'updated'),
  R.head,
  R.prop('fullName')
)

const editorText = (updatedAt: string, contributors: Playground.Contributor[]) =>
  `, last edited ${distanceInWords(updatedAt, new Date())} ago by ${editorName(contributors)}`

interface Props {
  id: Nullable<number>
  onClose(): void
}

const ObservationModal = ({ id, onClose }: Props) => {
  const serviceFkey = useSelectedServiceFkey()

  const [updateBookmark] = useMutation(UPDATE_OBSERVATION_BOOKMARK)

  const { data, error, loading } = useQuery(GET_OBSERVATION, {
    variables: { id, serviceFkey },
    skip: !id,
  })

  const modalHeaderButtons = useCallback(
    () => (
      <Flex alignItems="center" flexDirection="row">
        <ModalButton
          onClick={() => {
            onClose()
            navigate(`/observations/print/${id}`)
          }}
          icon={'printer'}
          text={'Print'}
        />

        <Can
          perform={obs.write}
          yes={() => (
            <ModalButton
              onClick={() => {
                onClose()
                navigate(`/observations/${id}/edit`)
              }}
              icon="editOutlined"
              text="Edit"
            />
          )}
        />
      </Flex>
    ),
    [id, onClose]
  )

  if (error) {
    if (is404(error)) {
      return <Error404 message="Observation not found" />
    }

    throw error
  }

  if (!id) {
    return null
  }

  const observation = data?.context?.observation as Playground.Observation | undefined

  if (loading || !observation) {
    return <DocumentModalPlaceholder onClose={onClose} />
  }

  const creator = observation.contributors?.find((x) => x.action === 'created')
  const bookmarked = observation.bookmarked
  const bookmarkIcon = observation.bookmarked ? 'bookmarkActive' : 'bookmark'

  const onBookmark = () =>
    updateBookmark({
      variables: {
        id,
        bookmarked: !bookmarked,
      },
    })
  const footer = (
    <Flex alignItems="center" justifyContent="space-between" width={1}>
      <Text.span fontSize={1} lineHeight={1.4} medium px={3}>
        {`Observed ${observedOnText(observation)} ago`}
        {isPlayground && editorText(observation.updatedAt, observation.contributors!)}
      </Text.span>
      <Flex alignItems="center" justifyContent="center" screenOnly>
        {isPlayground && (
          <SecondaryButton width="2px" onClick={onBookmark}>
            <Icon name={bookmarkIcon} />
          </SecondaryButton>
        )}

        <SecondaryButton
          mr={2}
          icon={<Icon name="fullscreen" fill="primary" />}
          onClick={() => navigate(`/observations/display/${observation.id}`)}>
          Full Screen
        </SecondaryButton>
      </Flex>
    </Flex>
  )

  return (
    <DocumentModal open={!!id} onClose={onClose} renderHeader={modalHeaderButtons}>
      <DocumentRenderer
        caption={observation.description}
        documentId={id}
        documentType="observation"
        footer={footer}
        futurePlanning={observation.futurePlanning}
        heading={observation.title}
        learningOutcomes={observation.learningOutcomes}
        overline={creator?.fullName}
        planningDate={observation.planningDate}
        taggedChildren={observation.children}
        learningContentAssets={observation.learningContentAssets}>
        <DocumentModalPreview>
          <MediaRenderer
            height={[STACKED_PREVIEW_HEIGHT, EXPANDED_PREVIEW_HEIGHT]}
            mediaItems={observation.media!}
            title={observation.title}
          />
        </DocumentModalPreview>
      </DocumentRenderer>
    </DocumentModal>
  )
}

ObservationModal.displayName = 'ObservationModal'

export default React.memo(ObservationModal)
